import {
  getCurrentUserProfile,
  updateHasAddedProperty,
  updateIsHomeowner,
} from '@/helpers/AccountsApiClient/ApiClient';
import {
  InteractionContainerName,
  InteractionEvent,
  InteractionGoal,
  trackEvent,
} from '@/helpers/GTM/GTMHelper';
import { computed, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { useStore } from 'vuex';

const HOW_VALUATION_ADD_PROPERTY_PATH_BY_LOCALE = {
  de: '/de/immobilienbewertung/bewertungsformular',
  fr: '/fr/estimation/formulaire-evaluation',
  it: '/it/valutazione/modulo-di-valutazione',
  en: '/en/property-valuation/valuation-form',
};

const HOW_VALUATION_LEARN_MORE_PATH_BY_LOCALE = {
  de: '/de/immobilienbewertung/eigentuemer',
  fr: '/fr/estimation/proprietaires',
  it: '/it/valutazione/proprietari',
  en: '/en/property-valuation/owners',
};

// eslint-disable-next-line max-statements -- composable: refs, computeds, handlers, and auth watch
export function usePropertyOwnership() {
  const store = useStore();
  const { locale } = useI18n();

  const isHomeowner = ref<boolean | undefined>(undefined);
  const howFavoritesCtaInteraction = ref(false);
  const hasFetchedOwnershipStatus = ref(false);
  const isOwnershipConfirmationToastVisible = ref(false);
  const isSavingOwnershipAnswer = ref(false);
  const isSavingAddProperty = ref(false);

  const isUserAuthenticated = computed(() => store.getters.isUserAuthenticated);

  const shouldShowOwnershipCard = computed(
    () =>
      isUserAuthenticated.value &&
      hasFetchedOwnershipStatus.value &&
      isHomeowner.value === undefined,
  );

  const shouldShowHomeownerWorldCta = computed(
    () =>
      isUserAuthenticated.value &&
      hasFetchedOwnershipStatus.value &&
      isHomeowner.value === true &&
      !howFavoritesCtaInteraction.value,
  );

  const shouldShowPropertyCard = computed(
    () => shouldShowOwnershipCard.value || shouldShowHomeownerWorldCta.value,
  );

  const accountSettingsUrl = computed(
    () => `${import.meta.env.VUE_APP_PLATFORM_ACCOUNT_BASE_URL}/${locale.value}/account-settings`,
  );

  const learnMoreUrl = computed(
    () =>
      `${import.meta.env.VUE_APP_BASE_URL}${HOW_VALUATION_LEARN_MORE_PATH_BY_LOCALE[locale.value]}?source=favourites`,
  );

  const fetchOwnershipStatus = async () => {
    if (!isUserAuthenticated.value) {
      hasFetchedOwnershipStatus.value = true;
      isHomeowner.value = undefined;
      howFavoritesCtaInteraction.value = false;
      return;
    }

    const profile = await getCurrentUserProfile();

    if (!isUserAuthenticated.value) {
      return;
    }

    isHomeowner.value = typeof profile?.isHomeowner === 'boolean' ? profile.isHomeowner : undefined;
    howFavoritesCtaInteraction.value = profile?.howFavoritesCtaInteraction === true;
    hasFetchedOwnershipStatus.value = true;
  };

  const handleOwnershipAnswer = async (answer: boolean) => {
    if (isSavingOwnershipAnswer.value) return;

    isSavingOwnershipAnswer.value = true;
    trackEvent(InteractionEvent.BUTTON_CLICK, {
      h_event_action_007: InteractionGoal.HOW_INPUT_IS_PROPERTY_OWNER,
      h_event_label_008: answer ? InteractionContainerName.TRUE : InteractionContainerName.FALSE,
    });
    const isSaved = await updateIsHomeowner(answer);
    isSavingOwnershipAnswer.value = false;

    if (!isSaved) return;

    isHomeowner.value = answer;

    if (!answer) {
      isOwnershipConfirmationToastVisible.value = true;
    }
  };

  const dismissOwnershipConfirmationToast = () => {
    isOwnershipConfirmationToastVisible.value = false;
  };

  const handleHomeownerWorldCtaClick = async () => {
    if (isSavingAddProperty.value) return;

    trackEvent(InteractionEvent.BUTTON_CLICK, {
      h_event_action_007: InteractionGoal.HOW_GO_TO_VALUATION_FORM,
      h_event_label_008: InteractionContainerName.FAVOURITES,
    });

    isSavingAddProperty.value = true;
    const didPersist = await updateHasAddedProperty(true);
    isSavingAddProperty.value = false;

    if (didPersist) {
      howFavoritesCtaInteraction.value = true;
    }

    window.open(
      `${import.meta.env.VUE_APP_BASE_URL}${HOW_VALUATION_ADD_PROPERTY_PATH_BY_LOCALE[locale.value]}?source=favourites`,
      '_blank',
      'noopener,noreferrer',
    );
  };

  const handleLearnMoreClick = () => {
    trackEvent(InteractionEvent.BUTTON_CLICK, {
      h_event_action_007: InteractionGoal.HOW_GO_TO_MY_REAL_ESTATE,
      h_event_label_008: InteractionContainerName.FAVOURITES,
    });
  };

  watch(
    isUserAuthenticated,
    async (isAuthenticated) => {
      if (isAuthenticated) {
        await fetchOwnershipStatus();
        return;
      }

      hasFetchedOwnershipStatus.value = false;
      isHomeowner.value = undefined;
      howFavoritesCtaInteraction.value = false;
    },
    { immediate: true },
  );

  return {
    accountSettingsUrl,
    dismissOwnershipConfirmationToast,
    handleOwnershipAnswer,
    handleHomeownerWorldCtaClick,
    handleLearnMoreClick,
    isOwnershipConfirmationToastVisible,
    isSavingAddProperty,
    isSavingOwnershipAnswer,
    learnMoreUrl,
    shouldShowPropertyCard,
    shouldShowOwnershipCard,
    shouldShowHomeownerWorldCta,
    fetchOwnershipStatus,
  };
}
